import { UniversalBurgerMenu } from '@apps/admin/components/UniversalBurgerMenu/UniversalBurgerMenu';
import { DashboardChecklist } from '@apps/admin/routes/DashboardPreview/components/DashboardChecklist';
import { JoyLogo } from '@assets/index';
import { useTranslation } from '@shared/core';
import { animationTransition } from '@shared/utils/animationTransition';
import { useScrollPosition } from '@shared/utils/hooks/useScrollPosition';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { Hamburger, Share, WebGlobe, Help } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useState } from 'react';
import { DashboardAccordion } from './components/DashboardAccordion';
import { useDashboardContentV3Telemetry } from './DashboardContentV3.telemetry';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { EventInfoColumn } from './components/EventInfoColumn';
import { HeaderHelpMenu } from '@apps/admin/components/HeaderHelpMenu';

export const DashboardContentV3 = ({
  eventHandle,
  eventId,
  onShareDialogOpen,
  onProductSelectionOpen,
  fixedHeaderOnMobile
}: {
  eventHandle: string;
  eventId: string;
  onShareDialogOpen: () => void;
  onProductSelectionOpen: () => void;
  fixedHeaderOnMobile?: boolean;
}) => {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  const telemetry = useDashboardContentV3Telemetry();

  const { t2 } = useTranslation('adminDashboardV3');
  const mainHeaderTranslations = t2('mainHeader');

  const scrollPosition = useScrollPosition(false, scrollElement);
  const scrollThreshold = scrollPosition <= 24;

  const isMobile = useMediaQuery(theme => theme.mediaQueries.down({ breakpointAlias: 'sm4' }));

  const handleHeaderLinkClick = (link: 'preview' | 'share' | 'support', cb?: () => void) => {
    telemetry.clickHeaderLink(link);
    cb?.();
  };

  return (
    <Box width={'100%'} height={'100%'} overflowY={'auto'} backgroundColor={'mono1'} ref={e => setScrollElement(e)}>
      <Flex
        position={{
          md: 'fixed'
        }}
        width={'100%'}
        height={{
          _: pxToRem(88),
          md: scrollThreshold ? pxToRem(84) : pxToRem(56)
        }}
        transition={animationTransition('height')}
        paddingX={{
          _: 6,
          md: 7
        }}
        backdropFilter={{
          md: 'blur(12px)'
        }}
        alignItems={'center'}
        justifyContent={'space-between'}
        zIndex={10}
      >
        <Box>
          <Flex
            alignItems={'center'}
            columnGap={4}
            position={
              fixedHeaderOnMobile
                ? {
                    _: 'fixed',
                    md: 'static'
                  }
                : 'static'
            }
            zIndex={1}
            paddingY={{
              _: 3,
              md: 0
            }}
            paddingX={{
              _: 4,
              md: 0
            }}
            borderRadius={{
              _: 5,
              md: 0
            }}
            backdropFilter={{
              _: 'blur(12px)',
              md: 'none'
            }}
            left={pxToRem(20)}
            top={pxToRem(20)}
          >
            <UniversalBurgerMenu>
              <Hamburger size={'lg'} />
            </UniversalBurgerMenu>
            <JoyLogo height={pxToRem(30)} width={pxToRem(47)} />
            {!isMobile && <TextV2 typographyVariant={'hed5'}>{mainHeaderTranslations.title}</TextV2>}
          </Flex>
        </Box>
        <Flex>
          <ButtonV2
            size="sm"
            variant="ghost"
            intent="neutral"
            startIcon={<WebGlobe size={'sm'} />}
            as={'a'}
            href={`/${eventHandle}`}
            target={'_blank'}
            onClick={() => handleHeaderLinkClick('preview')}
          >
            {scrollThreshold && !isMobile && mainHeaderTranslations.previewCta}
          </ButtonV2>
          <ButtonV2 size="sm" variant="ghost" intent="neutral" startIcon={<Share size={'sm'} />} onClick={() => handleHeaderLinkClick('share', onShareDialogOpen)}>
            {scrollThreshold && !isMobile && mainHeaderTranslations.shareCta}
          </ButtonV2>
          <HeaderHelpMenu
            renderButton={toggleMenu => (
              <ButtonV2
                size="sm"
                variant="ghost"
                intent="neutral"
                startIcon={<Help size={'sm'} />}
                onClick={() => {
                  toggleMenu();
                  handleHeaderLinkClick('support');
                }}
              >
                {scrollThreshold && !isMobile && mainHeaderTranslations.supportCta}
              </ButtonV2>
            )}
          />
        </Flex>
      </Flex>
      <Flex
        columnGap={pxToRem(96)}
        paddingTop={{
          md: pxToRem(84)
        }}
        justifyContent={'center'}
        maxWidth={{
          _: pxToRem(446),
          md: '100%'
        }}
        margin={{
          _: 'auto',
          md: 'unset'
        }}
        flexDirection={{
          _: 'column',
          md: 'row'
        }}
        paddingX={{
          _: 6,
          md: 0
        }}
      >
        <Box
          width={{
            md: '50%'
          }}
          maxWidth={{
            md: pxToRem(446)
          }}
          paddingY={{
            md: pxToRem(60)
          }}
          position={{
            md: 'sticky'
          }}
          top={0}
          height={{
            md: '100%'
          }}
        >
          <EventInfoColumn eventHandle={eventHandle} />
        </Box>
        <Box
          maxWidth={{
            md: pxToRem(446)
          }}
          width={{
            md: '50%'
          }}
          paddingTop={{
            _: 7,
            md: 0
          }}
          paddingBottom={10}
        >
          <Flex
            flexDirection={'column'}
            paddingX={{
              md: 5
            }}
            rowGap={7}
            alignItems={'center'}
          >
            <DashboardChecklist eventHandle={eventHandle} eventId={eventId} />
            <DashboardAccordion eventHandle={eventHandle} eventId={eventId} onProductSelectionOpen={onProductSelectionOpen} />
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};
